import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
  computed,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { forkJoin } from 'rxjs';
import { JobRequestsService } from '../../../services/job-requests.service';
import { MaterialsService } from '../../../services/materials.service';
import { WorksheetService } from '../../../services/worksheet.service';
import { SystemSettingsService } from '../../../services/system-settings.service';
import { MaterialListRowDto } from '@models/material-list-row-dto.interface';
import { TestTypeLookupDto } from '@models/test-type-lookup.interface';
import { CreateMudCheckDto } from '@models/create-mud-check-dto.interface';
import { WorksheetDto } from '@models/worksheet-dto.interface';
import { SearchParameters } from '../../../shared/models/search-parameters.model';
import { MaterialListQuery } from '@models/material-list-query.interface';
import { FormsModule } from '@angular/forms';
import { of, switchMap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { EditChemicalComponent, EditChemicalDialogData } from '../../chemicals/edit-chemical/edit-chemical.component';
import { EditMaterialDialogComponent } from '../../inventory/inventory-dialog/edit-material-dialog/edit-material-dialog.component';

export interface MudCheckWizardData {
  jobId: number;
}

@Component({
  selector: 'app-mud-check-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatStepperModule,
    MatCheckboxModule,
    FormsModule,
    MatIconModule
  ],
  templateUrl: './mud-check-wizard.component.html',
  styleUrl: './mud-check-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MudCheckWizardComponent implements OnInit {
  private readonly dialogMaterials = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<MudCheckWizardComponent>);
  readonly data = inject<MudCheckWizardData>(MAT_DIALOG_DATA);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly materialsService = inject(MaterialsService);
  private readonly worksheetService = inject(WorksheetService);
  private readonly systemSettingsService = inject(SystemSettingsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected isLoadingInitial = signal(true);
  protected isSearching = signal(false);
  protected isSaving = signal(false);

  protected materials = signal<MaterialListRowDto[]>([]);
  protected selectedMaterial = signal<MaterialListRowDto | null>(null);
  protected testTypes = signal<TestTypeLookupDto[]>([]);
  protected selectedTestTypeIds = signal<Set<number>>(new Set());
  selectedMudcheckType: string = 'NAF Mudcheck';

  protected materialFilterControl = this.fb.control<string | null>(null);

  private fluidConstants = { bariteSgconst: null as number | null, baseFluidSgconst: null as number | null, lowGravitySolidsSgconst: null as number | null };

  protected infoForm = this.fb.group({
    startDate: [new Date().toISOString().split('T')[0]],
    sapCode:   [null as string | null, Validators.maxLength(50)]
  });


  protected get canFinish(): boolean {
    return this.selectedTestTypeIds().size > 0 && !this.isSaving();
  }

  protected get stepTwoValid(): boolean {
    return this.selectedTestTypeIds().size > 0;
  }

  ngOnInit(): void {
  forkJoin({
    testTypes: this.jobRequestsService.getMudCheckTestTypes(),
  })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: ({ testTypes }) => {
        this.testTypes.set(testTypes);
        this.updateDefaultTestTypes(testTypes);

        this.loadMaterials();
        this.isLoadingInitial.set(false);
      },
      error: () => {
        this.isLoadingInitial.set(false);
      },
    });

    // Load fluid constants from system settings in the background
    this.systemSettingsService.getFluidConstants('WBM').subscribe({
      next: (c) => {
        this.fluidConstants = {
          bariteSgconst: c.bariteSG,
          baseFluidSgconst: c.baseFluidSG,
          lowGravitySolidsSgconst: c.lowGravitySolidsSG,
        };
      },
    });
  }

  private loadMaterials(): void {
    const searchParams = this.buildSearchParams('');

    this.materialsService
      .searchMaterials(searchParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (materials) => {
          this.materials.set(materials.items);
        },
      });
  }

  refreshMaterials(): void {
    this.loadMaterials();
  }
  

  private updateDefaultTestTypes(testTypes: TestTypeLookupDto[]): void {
    const defaults = new Set(
        testTypes
        .filter(tt =>
            this.selectedMudcheckType === 'NAF Mudcheck'
            ? tt.isDefaultForNafMudCheck
            : tt.isDefaultForWbmMudCheck
        )
        .map(tt => tt.id)
    );

    this.selectedTestTypeIds.set(defaults);
  }

  protected onAddNewFluid(): void {
    this.dialogMaterials
          .open(EditMaterialDialogComponent, {
            width: '750px',
            minWidth: '750px',
            maxHeight: '90vh',
            data: {},
          })
          .afterClosed()
          .subscribe((newId: number | undefined) => {
            if (newId) {
              this.loadMaterials();
            }
          });
  }

  protected onSearch(): void {
    const filter = this.materialFilterControl.value ?? '';
    this.isSearching.set(true);
    this.materialsService
      .searchMaterials(this.buildSearchParams(filter))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.materials.set(result.items);
          this.isSearching.set(false);
        },
        error: () => this.isSearching.set(false),
      });
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  protected onSelectMaterial(material: MaterialListRowDto): void {
    this.selectedMaterial.set(
      this.selectedMaterial()?.id === material.id ? null : material
    );
  }

  protected onDoubleClickMaterial(material: MaterialListRowDto, stepper: { next: () => void }): void {
    this.selectedMaterial.set(material);
    stepper.next();
  }

  protected toggleTestType(id: number): void {
    const current = new Set(this.selectedTestTypeIds());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.selectedTestTypeIds.set(current);
  }

  protected isTestTypeSelected(id: number): boolean {
    return this.selectedTestTypeIds().has(id);
  }

  protected onFinish(): void {
    this.submitMudCheck(false, null);
  }

  protected onPrintWorksheet(): void {
    if (!this.canFinish || this.isSaving()) return;

    // Open the print tab synchronously within the click handler so the browser
    // popup blocker does not cancel it later (the mud check creation and
    // worksheet lookup are async and would otherwise open the tab outside a
    // user gesture).
    const printWindow = window.open('', '_blank');
    this.submitMudCheck(true, printWindow);
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  private buildSearchParams(filter: string): SearchParameters<MaterialListQuery> {
    return {
      query: { filter: filter || null },
      pageIndex: 0,
      pageSize: 50,
    };
  }

  private submitMudCheck(openWorksheet: boolean, printWindow: Window | null): void {
    if (!this.canFinish || this.isSaving()) {
      printWindow?.close();
      return;
    }

    const raw = this.infoForm.getRawValue();
    const material = this.selectedMaterial();
    const dto: CreateMudCheckDto = {
      materialId: material?.id ?? null,
      testTypeIds: Array.from(this.selectedTestTypeIds()),
      startDate: raw.startDate ?? null,
      sapCode: raw.sapCode?.trim() ?? null,
      bariteSgconst: material?.bariteSpecificGravity ?? this.fluidConstants.bariteSgconst,
      baseFluidSgconst: material?.baseFluidSpecificGravity ?? this.fluidConstants.baseFluidSgconst,
      lowGravitySolidsSgconst: material?.lowGravitySolidsSpecificGravity ?? this.fluidConstants.lowGravitySolidsSgconst,
    };

    this.isSaving.set(true);
    this.jobRequestsService
      .createMudCheck(this.data.jobId, dto)
      .pipe(
        switchMap(() => openWorksheet
          ? this.worksheetService.getJobWorksheets(this.data.jobId)
          : of<WorksheetDto[]>([])
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (worksheets) => {
          this.isSaving.set(false);

          if (openWorksheet) {
            const worksheet = this.findWorksheetForPrint(worksheets);
            if (worksheet) {
              const url = `/jobs/${this.data.jobId}/worksheets/${worksheet.testGroupId}`;
              if (printWindow) {
                printWindow.location.href = url;
              } else {
                window.open(url, '_blank');
              }
            } else {
              printWindow?.close();
              alert('Mud check was created, but worksheet was not found.');
            }
          }

          this.dialogRef.close(true);
        },
        error: () => {
          this.isSaving.set(false);
          printWindow?.close();
          alert('Failed to create mud check. Please try again.');
        },
      });
  }

  private findWorksheetForPrint(worksheets: WorksheetDto[]): WorksheetDto | null {
    if (worksheets.length === 0) {
      return null;
    }

    const mudCheckWorksheets = worksheets.filter((ws) =>
      (ws.title ?? '').toLowerCase().includes('mud check')
    );
    const candidates = mudCheckWorksheets.length > 0 ? mudCheckWorksheets : worksheets;

    return candidates.reduce<WorksheetDto | null>((latest, current) => {
      if (!latest || current.testGroupId > latest.testGroupId) {
        return current;
      }
      return latest;
    }, null);
  }

  protected onMudcheckTypeChange(): void {
    this.updateDefaultTestTypes(this.testTypes());
  }
}
